<template>
  <div class="profile-view">
    <h2>User Profile</h2>
    <div v-if="email" class="profile-grid">
      <div class="details-col">
        <p><strong>Email:</strong> {{ email }} <span v-if="emailVerified" class="verified">(verified)</span></p>
        <p><strong>Name:</strong> {{ displayName }}</p>
        <p v-if="createdAt"><strong>Joined:</strong> {{ createdAtStr }}</p>
        <p v-if="lastSignIn"><strong>Last sign-in:</strong> {{ lastSignIn }}</p>
        <p v-if="providerList.length"><strong>Providers:</strong> {{ providerList.join(', ') }}</p>
        <div class="actions">
          <button class="emailbtn" @click="sendReset">Reset password</button>
          <button class="emailbtn" @click="sendVerification" v-if="!emailVerified">Send verification email</button>
        </div>
      </div>
    </div>
    <div v-else>
      <p>You are not signed in.</p>
      <router-link to="/signin">Sign in</router-link>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert';
import { auth, resetPassword, firestore, sendVerificationEmail } from '../firebase/firebase.utils';

export default {
  name: 'Profile',
  data() {
    return {
      email: null,
      uid: null,
      displayName: null,
      createdAt: null,
      unsubscribeAuth: null,
      providerList: [],
      lastSignIn: null,
      emailVerified: false,
    };
  },
  computed: {
    createdAtStr() {
      return this.createdAt ? new Date(this.createdAt).toLocaleString() : null;
    },
  },
  async mounted() {
    this.unsubscribeAuth = auth.onAuthStateChanged(() => {
      this.loadUser();
    });
    await this.loadUser();
  },
  beforeDestroy() {
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
  methods: {
    async loadUser() {
      const user = auth.currentUser;
      if (!user) {
        this.email = null;
        this.uid = null;
        this.displayName = null;
        this.createdAt = null;
        return;
      }
      this.email = user.email || null;
      this.uid = user.uid || null;
      this.displayName = user.displayName || user.email || 'No name provided';
      this.emailVerified = !!user.emailVerified;
      this.providerList = (user.providerData || [])
        .map(p => {
          const id = p.providerId || '';
          if (id === 'google.com') return 'Google';
          if (id === 'facebook.com') return 'Facebook';
          if (id === 'twitter.com') return 'Twitter';
          if (id === 'github.com') return 'GitHub';
          if (id === 'password') return 'Email';
          if (id === 'phone') return 'Phone';
          return id.replace('.com', '');
        });
      if (!this.providerList.length && this.email) {
        this.providerList = ['Email'];
      }
      this.lastSignIn = user.metadata && user.metadata.lastSignInTime ? user.metadata.lastSignInTime : null;
      try {
        const userRef = firestore.doc(`users/${user.uid}`);
        const snap = await userRef.get();
        if (snap.exists) {
          const data = snap.data();
          if (data.createdAt) {
            // handle Firestore Timestamp or JS Date
            this.createdAt = data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt;
          }
          // if displayName not on auth, check stored profile
          if (!this.displayName && data.displayName) this.displayName = data.displayName;
        }
      } catch (err) {
        console.error('Error loading user profile', err);
      }
    },
    async sendReset() {
      if (!this.email) {
        return swal('Not signed in', 'Please sign in to send a reset email.', 'warning');
      }
      const res = await resetPassword(this.email);
      if (res.ok) {
        swal('Reset email sent', `Check ${this.email} for a password reset link.`, 'success');
      } else {
        swal('Reset failed', res.error && res.error.message ? res.error.message : 'Unable to send reset email.', 'error');
      }
    },
    async sendVerification() {
      try {
        const res = await sendVerificationEmail();
        if (res.ok) swal('Verification sent', `Check ${this.email} for verification link.`, 'success');
        else swal('Failed', res.error && res.error.message ? res.error.message : 'Unable to send verification email', 'error');
      } catch (err) {
        swal('Failed', err.message || err, 'error');
      }
    },
  },
};
</script>

<style scoped>
.profile-view {
  max-width: 480px;
  margin: 24px auto;
  padding: 16px;
}
.actions button {
  margin-right: 0.75rem;
}
.actions button:last-child {
  margin-right: 0;
}
</style>
